import { IAppCustomDates } from "../../../utils/types";
import { BillStatus } from "../../billing/types/billing.types";
import { ClaimStatus } from "../../claims/types/claims.types";
import {
    NoteStatus,
    NoteTypes,
    SessionServiceType,
} from "../../notes/types/notes.types";

export enum DayInterval {
    ALL_TIME = "ALL_TIME",
    DAY_1_TO_30_DAYS = "DAY_1_TO_30_DAYS",
    DAY_31_TO_60_DAYS = "DAY_31_TO_60_DAYS",
    DAY_61_TO_90_DAYS = "DAY_61_TO_90_DAYS",
    DAY_91_TO_120_DAYS = "DAY_91_TO_120_DAYS",
    GREATER_THAN_120_DAYS = "GREATER_THAN_120_DAYS",
}

export interface ClientStatementSummary {
    client_name: string;
    client_id: string;
    total_amount_outstanding: number;
    amount_outstanding_ins: number;
    amount_outstanding_coins: number;
    total_amount_collected: number;
    amount_collected_ins: number;
    amount_collected_coins: number;
    total_amount_charged: number;
    amount_charged_ins: number;
    amount_charged_coins: number;
    total_amount_refunded: number;
    amount_refunded_ins: number;
    amount_refunded_coins: number;
    total_amount_written_off: number;
    amount_written_off_ins: number;
    amount_written_off_coins: number;
    insurance_provider?: string | null;
}
export interface ClientStatementBreakdown {
    date_of_service: string;
    cpt_code: string;
    note_type: string;
    start_date_time: string;
    end_date_time: string;
    invoice_id: string;
    insurance_payment_date: string;
    insurance_payment_amount: number;
    coinsurance_payment_date: string;
    coinsurance_payment_amount: number;
    insurance_amount_charged: number;
    coinsurance_amount_charged: number;
    insurance_provider: string;
    description: string;
    pr100_refunded_payment_amount?: number;
    pr100_refunded_payment_date?: string;
}

export interface IStatementOfAccountPreviewResponse {
    client_statements: {
        [client_id: string]: {
            statement_summary: ClientStatementSummary;
            statement_breakdown: ClientStatementBreakdown[];
            description: string;
        };
    };
}

export interface IStatementOfAccountFilters {
    action: string;
    client_ids: string[];
    provider_ids?: string[];
    insurance_provider_ids?: string[];
    custom_date: IAppCustomDates;
    from_date?: string | null;
    to_date?: string | null;
    from_amount_outstanding_ins?: string;
    to_amount_outstanding_ins?: string;
    from_amount_outstanding_coins?: string;
    to_amount_outstanding_coins?: string;
    send_to_client_email?: boolean;
    only_coins_outstanding?: boolean;
    summary_only?: boolean;
    page?: number;
    limit?: number;
}

export interface IExportStatementOfAccountFilters {
    client_ids: string[];
    provider_ids?: string[];
    insurance_provider_ids?: string[];
    custom_date?: IAppCustomDates;
    from_date?: string | null;
    to_date?: string | null;
    only_coins_outstanding?: boolean;
}

export interface IStatementOfAccount {
    statement_summary: ClientStatementSummary;
    statement_breakdown: ClientStatementBreakdown[];
    description: string;
}

export interface ISendStatementOfAccount {
    emails?: string[];
}

export interface ISessionActivity {
    provider_id: string;
    author_user_id: string;
    last_name: string;
    first_name: string;
    credential: string;
    supervisor_provider_id: string;
    supervisor_last_name: string;
    supervisor_first_name: string;
    no_of_clients: number;
    sessions_completed: number;
    sessions_cancelled: number;
    notes_completed: number;
    notes_due: number;
    notes_in_draft: number;
    notes_pending_changes: number;
    notes_pending_review: number;
}

export interface ISessionActivityResponse {
    from_date: string;
    to_date: string;
    summary: {
        total_no_of_clients: number;
        total_sessions_completed: number;
        total_notes_completed: number;
        total_notes_in_draft: number;
        total_notes_pending_changes: number;
        total_notes_pending_review: number;
        total_sessions_cancelled: number;
    };
    rows: ISessionActivity[];
}

export interface IProviderSessionActivityItem {
    note_id?: string;
    client_id: string;
    service: SessionServiceType;
    client: {
        client_id: string;
        first_name: string;
        last_name: string;
    };
    date_of_service: string;
    note_type: string;
    note_status?: NoteStatus;
    start_date_time: string;
    end_date_time: string;
    duration_secs: number;
    is_cancelled: boolean;
    is_note_late: boolean;
    created_at: string;
}

export interface IProviderSessionStatsResponse {
    from_date: string;
    to_date: string;
    activity_list: IProviderSessionActivityItem[];
}

export interface ISessionActivityFilters {
    provider_name?: string;
    provider_credential?: string;
    custom_date?: string;
    from_date?: string | null;
    to_date?: string | null;
    note_status?: string[];
    from_created_at_date?: string | null;
    to_created_at_date?: string | null;
    include_non_billable_notes?: string;
}

export interface IProviderSessionStatsFilters {
    custom_date?: string;
    from_date?: string | null;
    to_date?: string | null;
    note_status?: string[];
    from_created_at_date?: string | null;
    to_created_at_date?: string | null;
    include_non_billable_notes?: string;
}

export interface IAgingReportResponse {
    date_of_service: string;
    collected: number;
    amount_outstanding: number;
    insurance_amount: number;
    invoice_id: string;
    coinsurance_amount: number;
    charge_amount: number;
    claim_external_reference_id: string;
    cpt_code: string;
    pending_settlement: number;
    invoice_status: BillStatus;
    insurance_provider_name?: string;
    note_id: string;
    note_url: string;
    submitted_on: string;
    provider: {
        user_id: string;
        provider_id: string;
        first_name: string;
        last_name: string;
        npi: string;
    };
    client: {
        user_id: string;
        client_id: string;
        first_name: string;
        last_name: string;
        date_of_birth: string;
    };
    diagnosis_codes: string[];
    age: number;
}

export interface ITerminatedClient {
    client_first_name: string;
    client_last_name: string;
    session_date: string;
    start_date_time: string;
    end_date_time: string;
    provider_first_name: string;
    provider_last_name: string;
    service: string;
    insurance_provider_name: string;
}

export interface ITerminatedClientsFilters {
    from_date?: string | null;
    to_date?: string | null;
    client_id?: string;
    insurance_provider_ids?: string[];
    provider_id?: string;
    search_string?: string;
    page?: number;
    limit?: number;
}

export interface IConfirmationReport {
    session_id: string;
    session_start_hr: number;
    session_end_hr: number;
    session_start_min: number;
    session_end_min: number;
    session_day: string;
    session_service: SessionServiceType;
    session_status: string;
    session_recurrence_rule: string;
    session_delivery_option: string;
    session_start_date: string;
    session_confirmed_date_on_intake: string;
    session_metadata: {
        is_paused: boolean;
    };
    client: {
        practice_id: string;
        client_id: string;
        client_first_name: string;
        client_last_name: string;
        client_email: string;
    };
    provider: {
        provider_id: string;
        provider_first_name: string;
        provider_last_name: string;
        provider_email: string;
    };
}

export interface IConfirmationReportFilters {
    search_string?: string;
    confirmed_start_date_time?: string | null;
    confirmed_end_date_time?: string | null;
    recurrence_rule?: string;
    client_id?: string;
    provider_id?: string;
    session_status?: string;
    page?: number;
    limit?: number;
    sort_attribute?: string;
    sort_order?: string;
    endpoint_version?: string;
}

export interface INoteAuditReportFilters {
    provider_name?: string;
    from_date?: string | null;
    to_date?: string | null;
    from_created_at_date?: string | null;
    to_created_at_date?: string | null;
    provider_credential?: string;
    custom_date?: string;
    include_non_billable_notes?: string;
}

export interface INoteAuditReport {
    from_date: string;
    to_date: string;
    summary: {
        total_no_of_clients: number;
        total_sessions_completed: number;
        total_notes_completed: number;
        total_notes_in_draft: number;
        total_notes_pending_changes: number;
        total_notes_pending_review: number;
        total_late_notes: number;
        total_no_of_changes_requested: number;
    };
    rows: INoteAuditReportRow[];
}

export interface INoteAuditReportRow {
    provider_id: string;
    last_name: string;
    first_name: string;
    credential: string;
    supervisor_provider_id: string;
    supervisor_last_name: string;
    supervisor_first_name: string;
    no_of_clients: number;
    session_completed: number;
    notes_completed: number;
    notes_in_draft: number;
    notes_pending_changes: number;
    notes_pending_review: number;
    late_notes: number;
    no_of_changes_requested: number;
}

export interface IProviderNoteAuditReport {
    activity_list: {
        client: {
            client_id: string;
            first_name: string;
            last_name: string;
        };
        session_history_id: string;
        client_id: string;
        date_of_service: string;
        service: SessionServiceType;
        note_status: NoteStatus;
        start_date_time: string;
        end_date_time: string;
        duration_secs: string;
        note_id: string;
        note_type: NoteTypes;
        coinsurance_charged_status: string | null;
        created_at: string;
        client_chart_number: string;
        self_pay: boolean;
        insurance_provider_name: string;
        is_note_late: boolean;
        changes_requested_count: string;
    }[];
}

export interface IAgingReportFilters {
    provider_ids?: string[];
    client_names?: string[];
    from_date?: string | null;
    to_date?: string | null;
    page?: number;
    limit?: number;
    sort_attribute?: string;
    sort_order?: string;
    custom_date?: string;
    cpt_codes?: string[];
    pos_codes?: string[];
    claim_statuses?: string[];
    bill_statuses?: string[];
    search_string?: string;
    insurance_provider_ids?: string[];
    remark_codes?: string[];
    exclude_zero_balance?: string;
    selected_columns?: string[];
}

export interface IAgingReport {
    client_first_name: string;
    client_last_name: string;
    date_of_birth: string | null;
    insurance_member_id?: string;
    provider_first_name: string;
    provider_last_name: string;
    date_of_service: string;
    client_chart_number: string;
    claim_external_reference_id: string | null;
    cpt_code: string;
    pos_code: string;
    modifier_codes: string;
    claim_billed_amount: number;
    patient_charged_amount: number;
    patient_writeoff_amount: number;
    client_paid_amount: number;
    insurance_paid_amount: number;
    insurance_writeoff_amount: number;
    total_writeoff_amount: number;
    claim_balance: number | null;
    claim_status: ClaimStatus | null;
    bill_status: BillStatus;
    remark_codes_payment: string;
    insurance_provider_name: string;
    pr100_amount: number;
    pr100_collected: number;
    claim_id: string | null;
    invoice_id: string;
    client_id: string;
    note_id: string;
    remark_codes: string | null;
    provider_npi?: string;
}

// Consent Reports Types
export enum ConsentStatus {
    NOT_SENT = "not_sent",
    SENT = "sent",
    RECEIVED = "received",
    NEEDS_CORRECTION = "needs_correction",
    COMPLETED = "completed",
    CANCELLED = "cancelled",
    PAUSED = "paused",
    NEEDS_RENEWAL = "needs_renewal",
}

export enum DateSource {
    INTAKE = "intake",
    SESSION = "session",
    MANUAL = "manual",
    CALCULATED = "calculated",
    SCHEDULED = "scheduled",
}

export interface IConsentDocument {
    document_id: string;
    document_url: string;
    document_name: string;
    uploaded_by: string;
    content_type: string;
    uploaded_at: string;
    document_type?: "generated_consent" | "form_attachment";
    field_id?: string;
    size?: number;
    page_count?: number;
    sha256?: string;
    page_audit?: IConsentPageAudit[];
}

export interface IConsentPageAudit {
    page_number: number;
    page_type: "consent" | "payment_card_authorization";
    timestamp: string;
    ip_address: string;
    user_agent: string;
    location?: string;
    tracking_method: "direct" | "inferred" | "submission";
}

export interface IConsentEvidence {
    evidence_id: string;
    ip_address: string;
    user_agent: string;
    step: string;
    timestamp: string;
    page_number?: number;
    page_type?: "consent" | "payment_card_authorization";
    location?: string;
}

export interface IConsentActivity {
    activity_id: string;
    timestamp: string;
    user_id: string;
    user_name: string;
    activity_type: string;
    description: string;
    metadata?: Record<string, unknown>;
}

export interface IConsentReport {
    consent_id: string;
    client_id: string;
    session_id: string;
    provider_id: string;
    status: ConsentStatus;
    start_date: string;
    start_date_source: DateSource;
    signed_date?: string;
    renewal_of_consent_id?: string;
    consent_documents: IConsentDocument[];
    activity_log: IConsentActivity[];
    created_at: string;
    updated_at: string;
    client: {
        client_first_name: string;
        client_last_name: string;
        client_email: string;
        client_phone?: string;
        client_date_of_birth: string;
    };
    provider: {
        provider_id: string;
        provider_first_name: string;
        provider_last_name: string;
        provider_email: string;
    };
    session: {
        recurrence_rule: string;
        session_status: string;
        delivery_option: string;
    };
    consent_form_sent_at?: string;
    intake_consent_form_rechecked_at?: string;
    intake_consult_call_notes?: string;
    consent_cc_notes?: string;
    client_submitted?: boolean;
    client_submitted_at?: string | null;
    payment_card_added_at?: string | null;
    evidences?: IConsentEvidence[];
}

export interface IConsentReportFilters {
    search_string?: string;
    client_id?: string;
    provider_id?: string;
    status?: ConsentStatus;
    start_date_from?: string;
    start_date_to?: string;
    signed_date_from?: string;
    signed_date_to?: string;
    session_status?: string;
    needs_renewal?: boolean;
    page?: number;
    limit?: number;
    sort_attribute?: string;
    sort_order?: string;
    selected_columns?: string[];
}

export interface ICollateralConsentReport {
    collateral_consent_id: string;
    consent_id: string;
    session_attendee_id: string;
    practice_id: string;
    client_id: string;
    client_name: string;
    session_client: {
        client_id: string;
        first_name: string;
        last_name: string;
    };
    session_id?: string;
    held_session_id?: string;
    intake_attendee_id?: string;
    attendee_name: string;
    first_name: string;
    last_name: string;
    email: string;
    date_of_birth?: string;
    relationship?: string;
    active: boolean;
    status: ConsentStatus;
    start_date?: string;
    good_form_on_file: boolean;
    rechecked_at?: string;
    sent_at?: string;
    signed_at?: string;
}

export interface ICollateralConsentReportFilters {
    search_string?: string;
    status?: ConsentStatus;
    start_date_from?: string;
    start_date_to?: string;
    date_sent_from?: string;
    date_sent_to?: string;
    signed_date_from?: string;
    signed_date_to?: string;
    include_inactive?: boolean;
    limit?: number;
    offset?: number;
    sort_attribute?: string;
    sort_order?: string;
}

export interface IUpdateCollateralConsentStatusRequest {
    status: ConsentStatus;
    completed_at?: string | null;
}

export interface ICreateConsentRequest {
    client_id: string;
    session_id: string;
    status?: ConsentStatus;
    start_date?: string;
    start_date_source?: DateSource;
    signed_date?: string;
    consent_form_sent_at?: string;
}

export interface IUpdateConsentRequest {
    status?: ConsentStatus;
    start_date?: string;
    start_date_source?: DateSource;
    signed_date?: string;
    activity_note?: string;
    correction_reason?: string;
}

export interface IRenewConsentRequest {
    session_id: string;
}

export interface IConsentsNeedingRenewalResponse {
    data: IConsentReport[];
    count: number;
    total_count: number;
    current_page: number;
    total_pages: number;
}

export interface IClientActiveSession {
    session_id: string;
    provider_id: string;
    provider_name: string;
    provider_email: string;
    service: string;
    recurrence_rule: string;
    status: string;
    start_date: string;
}

export interface IExpiringConsent {
    consent_id: string;
    client_id: string;
    client: {
        client_first_name: string;
        client_last_name: string;
        client_email: string;
        client_phone: string;
        client_date_of_birth: string;
    };
    session_id: string;
    provider: {
        provider_id: string;
        provider_first_name: string;
        provider_last_name: string;
        provider_email: string;
    };
    session: {
        recurrence_rule: string;
        session_status: string;
        delivery_option: string;
    };
    status: string;
    start_date: string;
    start_date_source: string;
    consent_documents: IConsentDocument[];
    activity_log: IConsentActivity[];
    created_by: string;
    updated_by: string;
    created_at: string;
    updated_at: string;
    signed_date: string;
    renewal_of_consent_id: string;
    provider_id: string;
    consent_form_sent_at: null;
    intake_consent_form_rechecked_at: string | null;
    intake_consult_call_notes: string | null;
    consent_cc_notes: string | null;
    client_submitted?: boolean;
    client_submitted_at?: string | null;
    payment_card_added_at?: string | null;
    evidences?: IConsentEvidence[];
    date_of_birth: string;
    age_at_signing_in_months: number;
    age_in_months: number;
    expiry_date: string;
    days_until_expiry: number;
}

export enum AccommodationNoteCategory {
    THERAPY = "therapy",
    TESTING = "testing",
    MED = "med",
}

export enum ProviderAccommodationStatus {
    UPCOMING = "upcoming",
    ACTIVE = "active",
    ENDED = "ended",
}

export interface IProviderAccommodationFilters {
    provider_name?: string;
    page?: number;
    limit?: number;
}

export interface IProviderAccommodation {
    accommodation_id: string;
    provider_id: string;
    provider_name: string;
    window_hours: number;
    note_categories: AccommodationNoteCategory[];
    effective_from: string;
    effective_to: string | null;
    notes: string | null;
    status: ProviderAccommodationStatus;
    created_by: {
        user_id: string;
        first_name: string;
        last_name: string;
    };
    created_at: string;
    updated_at: string;
}

export interface IProviderAccommodationPayload {
    window_hours: number;
    note_categories: AccommodationNoteCategory[];
    effective_from: string;
    effective_to: string | null;
    notes: string | null;
}

export interface ICreateProviderAccommodationPayload
    extends IProviderAccommodationPayload {
    provider_id: string;
}
